import React, { useState, useEffect, useRef } from 'react';
import { EmpresaConfig, EMPRESA_PADRAO_INICIAL, EMPRESA_TWO_INICIAL } from '../../types/empresa';
import { empresaService } from '../../services/empresaService';
import { baixarFichaCadastralPdf, imprimirFichaCadastral } from '../../services/fichaCadastralPdfGenerator';
import { ConfiguracaoSaldoPropostas } from './ConfiguracaoSaldoPropostas';
import { 
  Building2, 
  Upload, 
  Trash2, 
  Phone, 
  Mail, 
  MessageSquare, 
  Globe, 
  MapPin, 
  CreditCard, 
  FileText, 
  Check, 
  AlertCircle, 
  RefreshCw, 
  Plus, 
  Printer, 
  Download,
  ExternalLink, 
  Copy, 
  ShieldCheck, 
  Truck, 
  Image as ImageIcon,
  CheckCircle2,
  X
} from 'lucide-react';

export function ConfiguracaoEmpresaSection() {
  const [empresas, setEmpresas] = useState<EmpresaConfig[]>([]);
  const [empresaSelecionadaId, setEmpresaSelecionadaId] = useState<string>('');
  const [formData, setFormData] = useState<EmpresaConfig>(EMPRESA_PADRAO_INICIAL);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const [customLogoUrl, setCustomLogoUrl] = useState('');
  const [isFichaModalOpen, setIsFichaModalOpen] = useState(false);
  const [isExcluirModalOpen, setIsExcluirModalOpen] = useState(false);
  const [isExcluindo, setIsExcluindo] = useState(false);
  const [isRemoverLogoModalOpen, setIsRemoverLogoModalOpen] = useState(false);
  const [copiadoTexto, setCopiadoTexto] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carregar dados iniciais
  useEffect(() => {
    carregarEmpresas();
  }, []);

  const carregarEmpresas = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const lista = await empresaService.getEmpresas();
      setEmpresas(lista);

      const ativa = await empresaService.getEmpresaAtiva();
      if (ativa) {
        setEmpresaSelecionadaId(ativa.id);
        setFormData(ativa);
      } else if (lista.length > 0) {
        setEmpresaSelecionadaId(lista[0].id);
        setFormData(lista[0]);
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados das empresas:', err);
      setErrorMessage('Não foi possível carregar os dados das empresas.');
    } finally {
      setLoading(false);
    }
  };

  // Ao trocar de empresa no seletor
  const handleTrocarEmpresa = (id: string) => {
    setEmpresaSelecionadaId(id);
    const selecionada = empresas.find(e => e.id === id);
    if (selecionada) {
      setFormData(selecionada);
      setSaveSuccess(false);
      setErrorMessage(null);
    }
  };

  // Criar nova empresa/filial
  const handleNovaEmpresa = () => {
    const novoId = `empresa_${Date.now()}`;
    const nova: EmpresaConfig = {
      ...EMPRESA_PADRAO_INICIAL,
      id: novoId,
      is_padrao: false,
      razao_social: 'NOVA UNIDADE / FILIAL LTDA',
      nome_fantasia: 'Nova Filial',
      cnpj: '',
      logo_url: '',
      telefone: formData.telefone || '(11) 94711-5843',
      whatsapp: formData.whatsapp || '(11) 94711-5843',
      email: '',
      banco_padrao: '',
      chave_pix: ''
    };

    setEmpresas(prev => [...prev, nova]);
    setEmpresaSelecionadaId(novoId);
    setFormData(nova);
    setSaveSuccess(false);
    setErrorMessage(null);
  };

  // Solicitar exclusão da empresa atual
  const handleSolicitarExcluirEmpresa = () => {
    if (empresas.length <= 1) {
      setErrorMessage('O sistema precisa de pelo menos uma empresa cadastrada.');
      return;
    }
    setIsExcluirModalOpen(true);
  };

  // Confirmar exclusão da empresa
  const handleConfirmarExcluirEmpresa = async () => {
    setIsExcluindo(true);
    setErrorMessage(null);
    try {
      const res = await empresaService.deleteEmpresa(formData.id);
      if (res.success) {
        setIsExcluirModalOpen(false);
        setSaveSuccess(true);
        await carregarEmpresas();
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        setErrorMessage(res.error || 'Erro ao excluir empresa.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro inesperado ao excluir empresa.');
    } finally {
      setIsExcluindo(false);
    }
  };

  // Baixar Ficha Cadastral em PDF
  const handleBaixarPdf = async () => {
    setGerandoPdf(true);
    setErrorMessage(null);
    try {
      const res = await baixarFichaCadastralPdf(formData);
      if (!res.success) {
        setErrorMessage(res.error || 'Falha ao gerar o PDF da ficha cadastral.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro inesperado ao gerar PDF.');
    } finally {
      setGerandoPdf(false);
    }
  };

  // Imprimir Ficha Cadastral isolada
  const handleImprimirFicha = () => {
    try {
      imprimirFichaCadastral(formData);
    } catch (err: any) {
      setErrorMessage('Erro ao preparar impressão da ficha cadastral.');
    }
  };

  // Atualizar campo do formulário
  const handleChange = (campo: keyof EmpresaConfig, valor: any) => {
    setFormData(prev => ({
      ...prev,
      [campo]: valor
    }));
    setSaveSuccess(false);
  };

  // Salvar formulário
  const handleSalvar = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    setSaveSuccess(false);

    try {
      const res = await empresaService.saveEmpresa(formData);
      if (res.success && res.data) {
        setSaveSuccess(true);
        // Atualiza na lista local
        setEmpresas(prev => prev.map(emp => emp.id === res.data!.id ? res.data! : emp));
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        setErrorMessage(res.error || 'Erro ao salvar configurações da empresa.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro inesperado ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  // Definir empresa atual como padrão
  const handleDefinirComoPadrao = async () => {
    const atualizada: EmpresaConfig = {
      ...formData,
      is_padrao: true
    };
    setFormData(atualizada);
    await empresaService.saveEmpresa(atualizada);
    await empresaService.setEmpresaAtiva(atualizada.id);
    setEmpresas(prev => prev.map(e => ({
      ...e,
      is_padrao: e.id === atualizada.id
    })));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  // Upload de Logo
  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    setErrorMessage(null);
    try {
      const result = await empresaService.uploadLogo(file);
      if (result.success && result.url) {
        handleChange('logo_url', result.url);
      } else {
        setErrorMessage(result.error || 'Falha ao processar arquivo da logo.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro no upload da logo.');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Confirmar remoção de Logo
  const handleConfirmarRemoverLogo = () => {
    handleChange('logo_url', '');
    setIsRemoverLogoModalOpen(false);
  };

  // Salvar URL manual da logo
  const handleSalvarUrlLogo = () => {
    if (customLogoUrl.trim()) {
      handleChange('logo_url', customLogoUrl.trim());
      setIsUrlModalOpen(false);
      setCustomLogoUrl('');
    }
  };

  // Busca CEP automático
  const handleBuscarCep = async () => {
    if (!formData.cep) return;
    setBuscandoCep(true);
    setErrorMessage(null);
    const resultado = await empresaService.buscarCep(formData.cep);
    setBuscandoCep(false);

    if (resultado.error) {
      setErrorMessage(resultado.error);
      return;
    }

    setFormData(prev => ({
      ...prev,
      logradouro: resultado.logradouro || prev.logradouro,
      bairro: resultado.bairro || prev.bairro,
      cidade: resultado.cidade || prev.cidade,
      uf: resultado.uf || prev.uf,
      codigo_ibge: resultado.ibge || prev.codigo_ibge
    }));
  };

  // Copiar dados rápidos
  const handleCopiar = (texto: string, label: string) => {
    navigator.clipboard.writeText(texto);
    setCopiadoTexto(label);
    setTimeout(() => setCopiadoTexto(null), 2500);
  };

  // Abrir teste de WhatsApp
  const handleTestarWhatsapp = () => {
    const numeroLimpo = (formData.whatsapp || '').replace(/\D/g, '');
    if (!numeroLimpo) {
      setErrorMessage('Informe um número de WhatsApp válido antes de testar.');
      return;
    }
    const url = `https://wa.me/55${numeroLimpo}?text=${encodeURIComponent(`Olá, canal oficial de atendimento da ${formData.nome_fantasia || 'empresa'}.`)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-xs">
        <RefreshCw className="w-8 h-8 text-red-600 animate-spin mx-auto mb-3" />
        <p className="text-sm font-bold text-slate-700">Carregando configurações da empresa...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Barra de Seleção de Empresa & Ações Rápidas */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-red-50 text-red-600 rounded-xl">
              <Building2 className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-black text-slate-900">Configurações & Dados da Empresa</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Defina logo, contatos, dados cadastrais e fiscais utilizados em relatórios, ordens de serviço e propostas
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Seletor de Empresas */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
            {empresas.map(emp => (
              <button
                key={emp.id}
                type="button"
                onClick={() => handleTrocarEmpresa(emp.id)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  empresaSelecionadaId === emp.id
                    ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>{emp.nome_fantasia || emp.razao_social}</span>
                {emp.is_padrao && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500" title="Empresa Padrão Ativa" />
                )}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={handleNovaEmpresa}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
            title="Cadastrar nova empresa ou filial"
          >
            <Plus className="w-4 h-4 text-slate-600" />
            <span>Nova Filial</span>
          </button>

          <button
            type="button"
            onClick={() => setIsFichaModalOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
            title="Visualizar ficha cadastral completa da empresa"
          >
            <FileText className="w-4 h-4 text-indigo-600" />
            <span>Ficha Cadastral</span>
          </button>

          <button
            type="button"
            onClick={handleBaixarPdf}
            disabled={gerandoPdf}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition cursor-pointer disabled:opacity-60"
            title="Baixar Ficha Cadastral em PDF"
          >
            {gerandoPdf ? (
              <RefreshCw className="w-4 h-4 animate-spin text-emerald-700" />
            ) : (
              <Download className="w-4 h-4 text-emerald-700" />
            )}
            <span>{gerandoPdf ? 'Gerando PDF...' : 'Baixar PDF'}</span>
          </button>

          <button
            type="button"
            onClick={handleImprimirFicha}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
            title="Imprimir Ficha Cadastral da Empresa"
          >
            <Printer className="w-4 h-4 text-slate-600" />
            <span>Imprimir</span>
          </button>

          <button
            type="button"
            onClick={() => handleSalvar()}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition shadow-md shadow-red-600/20 cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Salvar Alterações</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Alertas de Feedback */}
      {saveSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl flex items-center justify-between text-xs font-bold animate-fadeIn">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>Configurações da empresa salvas com sucesso no banco de dados e sincronizadas no sistema!</span>
          </div>
          <button onClick={() => setSaveSuccess(false)} className="text-emerald-600 hover:text-emerald-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl flex items-center justify-between text-xs font-bold animate-fadeIn">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
            <span>{errorMessage}</span>
          </div>
          <button onClick={() => setErrorMessage(null)} className="text-rose-600 hover:text-rose-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Formulário Principal */}
      <form onSubmit={handleSalvar} className="space-y-6">
        
        {/* BLOCO 1: IDENTIDADE VISUAL & LOGO */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-amber-50 text-amber-600 rounded-lg">
                <ImageIcon className="w-4 h-4" />
              </span>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Logo da Empresa & Identidade Visual</h3>
                <p className="text-xs text-slate-500">
                  A logo é aplicada no cabeçalho do sistema, nas Ordens de Serviço (PDF) e nas Propostas Comerciais
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
              >
                <Upload className="w-3.5 h-3.5 text-slate-600" />
                <span>{uploadingLogo ? 'Enviando...' : 'Enviar Nova Imagem'}</span>
              </button>

              <button
                type="button"
                onClick={() => setIsUrlModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
              >
                <Globe className="w-3.5 h-3.5 text-slate-600" />
                <span>Inserir URL</span>
              </button>

              {formData.logo_url && (
                <button
                  type="button"
                  onClick={() => setIsRemoverLogoModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl transition cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Remover Logo</span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleUploadLogo}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            {/* Preview em Fundo Claro (Propostas & PDFs) */}
            <div className="p-4 rounded-xl border border-slate-200 bg-white flex flex-col items-center justify-center min-h-[160px] text-center relative group">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider absolute top-2 left-3">
                Visualização em Fundo Claro (Documentos & Impressão)
              </span>

              {formData.logo_url ? (
                <div className="my-auto py-2">
                  <img
                    src={formData.logo_url}
                    alt={formData.nome_fantasia || 'Logo da Empresa'}
                    className="max-h-24 max-w-full object-contain mx-auto"
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <div className="my-auto text-slate-400 py-4">
                  <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-1" />
                  <p className="text-xs font-medium">Nenhuma logo personalizada cadastrada.</p>
                  <p className="text-[11px] text-slate-400">O sistema utilizará a tipografia padrão com a marca.</p>
                </div>
              )}
            </div>

            {/* Preview em Fundo Escuro (Sidebar & Header) */}
            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900 flex flex-col items-center justify-center min-h-[160px] text-center relative">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider absolute top-2 left-3">
                Visualização na Barra Lateral (Menu Escuro)
              </span>

              {formData.logo_url ? (
                <div className="my-auto py-2">
                  <img
                    src={formData.logo_url}
                    alt={formData.nome_fantasia || 'Logo da Empresa'}
                    className="max-h-20 max-w-full object-contain mx-auto brightness-110"
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <div className="my-auto text-center py-4">
                  <div className="text-xl font-bold text-white tracking-tight uppercase">
                    {formData.nome_fantasia || 'VOOPE'}{' '}
                    <span className="text-red-500">TWO</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Identidade padrão do sistema</p>
                </div>
              )}
            </div>
          </div>
          <p className="text-[11px] text-slate-400">
            Dica: Recomendamos imagens com fundo transparente (PNG ou SVG) com dimensões próximas a 400x120 pixels.
          </p>
        </div>

        {/* BLOCO 2: IDENTIFICAÇÃO & DADOS CADASTRAIS/FISCAIS */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                <ShieldCheck className="w-4 h-4" />
              </span>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Identificação & Dados Fiscais</h3>
                <p className="text-xs text-slate-500">Informações jurídicas da empresa e registros de transportadora</p>
              </div>
            </div>

            {!formData.is_padrao && (
              <button
                type="button"
                onClick={handleDefinirComoPadrao}
                className="text-xs font-bold text-blue-700 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200 transition cursor-pointer"
              >
                Definir como Empresa Padrão do Sistema
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            {/* Razão Social */}
            <div className="sm:col-span-2">
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Razão Social <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.razao_social || ''}
                onChange={(e) => handleChange('razao_social', e.target.value)}
                placeholder="Ex.: VOOPE TRANSPORTES E LOGISTICA LTDA"
                className="w-full font-semibold rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white focus:border-red-500 text-slate-800"
              />
            </div>

            {/* Nome Fantasia */}
            <div className="sm:col-span-2">
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Nome Fantasia (Marca do Sistema) <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.nome_fantasia || ''}
                onChange={(e) => handleChange('nome_fantasia', e.target.value)}
                placeholder="Ex.: Voope Transportes"
                className="w-full font-semibold rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white focus:border-red-500 text-slate-800"
              />
            </div>

            {/* CNPJ */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                CNPJ <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.cnpj || ''}
                onChange={(e) => handleChange('cnpj', e.target.value)}
                placeholder="00.000.000/0000-00"
                className="w-full font-semibold rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white focus:border-red-500 text-slate-800"
              />
            </div>

            {/* Inscrição Estadual */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Inscrição Estadual (IE)
              </label>
              <input
                type="text"
                value={formData.inscricao_estadual || ''}
                onChange={(e) => handleChange('inscricao_estadual', e.target.value)}
                placeholder="Ex.: 142.890.123.110"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Inscrição Municipal */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Inscrição Municipal (IM)
              </label>
              <input
                type="text"
                value={formData.inscricao_municipal || ''}
                onChange={(e) => handleChange('inscricao_municipal', e.target.value)}
                placeholder="Ex.: 5.891.204-1"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* RNTRC / ANTT */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Truck className="w-3 h-3 text-red-600" /> Registro ANTT / RNTRC
              </label>
              <input
                type="text"
                value={formData.rntrc_antt || ''}
                onChange={(e) => handleChange('rntrc_antt', e.target.value)}
                placeholder="Ex.: 58492014"
                className="w-full font-semibold rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* CNAE Principal */}
            <div className="sm:col-span-2">
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                CNAE Principal
              </label>
              <input
                type="text"
                value={formData.cnae_principal || ''}
                onChange={(e) => handleChange('cnae_principal', e.target.value)}
                placeholder="Ex.: 49.30-2-02 - Transporte rodoviário de carga"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Regime Tributário */}
            <div className="sm:col-span-2">
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Regime Tributário
              </label>
              <select
                value={formData.regime_tributario || 'simples_nacional'}
                onChange={(e) => handleChange('regime_tributario', e.target.value)}
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              >
                <option value="simples_nacional">Simples Nacional</option>
                <option value="lucro_presumido">Lucro Presumido</option>
                <option value="lucro_real">Lucro Real</option>
                <option value="mei">Microempreendedor Individual (MEI)</option>
                <option value="outros">Outros Regimes</option>
              </select>
            </div>
          </div>
        </div>

        {/* BLOCO 3: CONTATOS OFICIAIS (TELEFONE, WHATSAPP, EMAIL) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
              <Phone className="w-4 h-4" />
            </span>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Canais de Comunicação & Contatos</h3>
              <p className="text-xs text-slate-500">Telefones, WhatsApp e e-mails que aparecem em ordens de serviço e contatos da empresa</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            {/* Telefone Fixo / Comercial */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Phone className="w-3 h-3 text-slate-500" /> Telefone Comercial <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.telefone || ''}
                onChange={(e) => handleChange('telefone', e.target.value)}
                placeholder="(11) 4711-5843"
                className="w-full font-semibold rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* WhatsApp Comercial */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                  <MessageSquare className="w-3 h-3 text-emerald-600" /> WhatsApp <span className="text-rose-600">*</span>
                </label>
                {formData.whatsapp && (
                  <button
                    type="button"
                    onClick={handleTestarWhatsapp}
                    className="text-[10px] font-bold text-emerald-700 hover:text-emerald-800 underline flex items-center gap-0.5 cursor-pointer"
                    title="Testar conversa no WhatsApp Web"
                  >
                    <span>Testar Link</span>
                    <ExternalLink className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
              <input
                type="text"
                required
                value={formData.whatsapp || ''}
                onChange={(e) => handleChange('whatsapp', e.target.value)}
                placeholder="(11) 94711-5843"
                className="w-full font-semibold rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* E-mail Geral */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-500" /> E-mail Principal <span className="text-rose-600">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="contato@voope.com.br"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* E-mail Financeiro */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3 text-indigo-500" /> E-mail Financeiro / Cobrança
              </label>
              <input
                type="email"
                value={formData.email_financeiro || ''}
                onChange={(e) => handleChange('email_financeiro', e.target.value)}
                placeholder="financeiro@voope.com.br"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* E-mail Operacional */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3 text-red-500" /> E-mail Operacional / Cargas
              </label>
              <input
                type="email"
                value={formData.email_operacional || ''}
                onChange={(e) => handleChange('email_operacional', e.target.value)}
                placeholder="operacional@voope.com.br"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Website / Portal */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Globe className="w-3 h-3 text-blue-500" /> Website Oficial
              </label>
              <input
                type="text"
                value={formData.website || ''}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://voope.com.br"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>
          </div>
        </div>

        {/* BLOCO 4: ENDEREÇO DA EMPRESA COM BUSCA CEP */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                <MapPin className="w-4 h-4" />
              </span>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Endereço & Sede Operacional</h3>
                <p className="text-xs text-slate-500">Localização física exibida em documentos fiscais e cadastrais</p>
              </div>
            </div>

            {formData.cep && (
              <button
                type="button"
                onClick={handleBuscarCep}
                disabled={buscandoCep}
                className="text-xs font-bold text-purple-700 hover:text-purple-800 bg-purple-50 px-3 py-1.5 rounded-lg border border-purple-200 transition cursor-pointer flex items-center gap-1"
              >
                {buscandoCep ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    <span>Consultando...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="w-3 h-3" />
                    <span>Autocompletar pelo CEP</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            {/* CEP */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                CEP <span className="text-rose-600">*</span>
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  required
                  value={formData.cep || ''}
                  onChange={(e) => handleChange('cep', e.target.value)}
                  placeholder="01310-100"
                  className="w-full font-semibold rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
                />
                <button
                  type="button"
                  onClick={handleBuscarCep}
                  disabled={buscandoCep}
                  className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition cursor-pointer"
                  title="Buscar endereço nos Correios"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${buscandoCep ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Logradouro */}
            <div className="sm:col-span-2">
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Logradouro (Rua, Avenida, Rodovia) <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.logradouro || ''}
                onChange={(e) => handleChange('logradouro', e.target.value)}
                placeholder="Ex.: Avenida Paulista"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Número */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Número <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.numero || ''}
                onChange={(e) => handleChange('numero', e.target.value)}
                placeholder="Ex.: 1000"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Complemento */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Complemento
              </label>
              <input
                type="text"
                value={formData.complemento || ''}
                onChange={(e) => handleChange('complemento', e.target.value)}
                placeholder="Sala, Andar, Galpão..."
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Bairro */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Bairro <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.bairro || ''}
                onChange={(e) => handleChange('bairro', e.target.value)}
                placeholder="Ex.: Bela Vista"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Cidade */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Cidade <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.cidade || ''}
                onChange={(e) => handleChange('cidade', e.target.value)}
                placeholder="São Paulo"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* UF */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Estado (UF) <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                maxLength={2}
                required
                value={formData.uf || ''}
                onChange={(e) => handleChange('uf', e.target.value.toUpperCase())}
                placeholder="SP"
                className="w-full font-bold uppercase rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>
          </div>
        </div>

        {/* BLOCO 5: DADOS BANCÁRIOS & CHAVE PIX */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
              <CreditCard className="w-4 h-4" />
            </span>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Dados Bancários & Chave PIX</h3>
              <p className="text-xs text-slate-500">Dados exibidos em faturas, ordens de serviço e cobranças</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            {/* Banco */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Banco Principal
              </label>
              <input
                type="text"
                value={formData.banco_padrao || ''}
                onChange={(e) => handleChange('banco_padrao', e.target.value)}
                placeholder="Ex.: Banco do Brasil, BTG Pactual"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Agência */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Agência
              </label>
              <input
                type="text"
                value={formData.agencia || ''}
                onChange={(e) => handleChange('agencia', e.target.value)}
                placeholder="0001-9"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Conta Corrente */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Conta Corrente
              </label>
              <input
                type="text"
                value={formData.conta_corrente || ''}
                onChange={(e) => handleChange('conta_corrente', e.target.value)}
                placeholder="123456-7"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Tipo Chave PIX */}
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Tipo de Chave PIX
              </label>
              <select
                value={formData.tipo_chave_pix || 'cnpj'}
                onChange={(e) => handleChange('tipo_chave_pix', e.target.value as any)}
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              >
                <option value="cnpj">CNPJ da Empresa</option>
                <option value="email">E-mail</option>
                <option value="telefone">Telefone / Celular</option>
                <option value="aleatoria">Chave Aleatória (EVP)</option>
              </select>
            </div>

            {/* Chave PIX */}
            <div className="sm:col-span-2">
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Chave PIX
              </label>
              <input
                type="text"
                value={formData.chave_pix || ''}
                onChange={(e) => handleChange('chave_pix', e.target.value)}
                placeholder="Chave PIX para recebimentos"
                className="w-full font-semibold text-emerald-800 rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            {/* Titular da Conta */}
            <div className="sm:col-span-2">
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Titular da Conta / Favorecido
              </label>
              <input
                type="text"
                value={formData.titular_conta || ''}
                onChange={(e) => handleChange('titular_conta', e.target.value)}
                placeholder="Nome completo ou Razão Social do titular"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>
          </div>
        </div>

        {/* BLOCO 6: TEXTOS & RODAPÉS PARA PROPOSTAS E DOCUMENTOS */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <span className="p-1.5 bg-slate-100 text-slate-700 rounded-lg">
              <FileText className="w-4 h-4" />
            </span>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Textos Padrão & Rodapés de Documentos</h3>
              <p className="text-xs text-slate-500">Configurações de rodapé aplicadas aos relatórios impressos e emitidos pelo sistema</p>
            </div>
          </div>

          <div className="space-y-3 text-xs">
            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Rodapé Padrão para Documentos
              </label>
              <input
                type="text"
                value={formData.rodape_documentos || ''}
                onChange={(e) => handleChange('rodape_documentos', e.target.value)}
                placeholder="Ex.: Voope Transportes • CNPJ: 23.059.737/0001-85 • Tel/WhatsApp: (11) 94711-5843 • www.voope.com.br"
                className="w-full font-medium rounded-xl border border-slate-300 px-3 py-2 bg-slate-50 focus:bg-white text-slate-800"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                Observações Operacionais / Políticas de Atendimento
              </label>
              <textarea
                rows={3}
                value={formData.observacoes_padrao || ''}
                onChange={(e) => handleChange('observacoes_padrao', e.target.value)}
                placeholder="Mensagem institucional ou termos padrão impressos em propostas..."
                className="w-full font-medium rounded-xl border border-slate-300 p-3 bg-slate-50 focus:bg-white text-slate-800 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Barra Flutuante ou Fixa de Ação Inferior */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          {empresas.length > 1 ? (
            <button
              type="button"
              onClick={handleSolicitarExcluirEmpresa}
              className="text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 px-3 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
              <span>Excluir esta empresa</span>
            </button>
          ) : (
            <div className="text-[11px] text-slate-400">
              * O sistema mantém pelo menos uma empresa cadastrada.
            </div>
          )}

          <div className="ml-auto flex items-center gap-2.5 flex-wrap">
            <button
              type="button"
              onClick={() => setIsFichaModalOpen(true)}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
            >
              <FileText className="w-4 h-4 text-indigo-600" />
              <span>Visualizar Ficha</span>
            </button>

            <button
              type="button"
              onClick={handleBaixarPdf}
              disabled={gerandoPdf}
              className="px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5 disabled:opacity-60"
            >
              {gerandoPdf ? (
                <RefreshCw className="w-4 h-4 animate-spin text-emerald-700" />
              ) : (
                <Download className="w-4 h-4 text-emerald-700" />
              )}
              <span>{gerandoPdf ? 'Gerando...' : 'Baixar PDF'}</span>
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition shadow-md shadow-red-600/20 cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Salvando Alterações...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Salvar Dados da Empresa</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Regras e Parâmetros Operacionais: Bloqueio de OS por Saldo */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <ConfiguracaoSaldoPropostas />
      </div>

      {/* MODAL: INSERIR URL DA LOGO */}
      {isUrlModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-600" />
                Inserir Link / URL da Logo
              </h3>
              <button onClick={() => setIsUrlModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 text-xs">
              <label className="font-bold text-slate-700 block">URL da Imagem (HTTPS):</label>
              <input
                type="url"
                placeholder="https://exemplo.com.br/logo.png"
                value={customLogoUrl}
                onChange={(e) => setCustomLogoUrl(e.target.value)}
                className="w-full rounded-xl border border-slate-300 p-2.5 bg-slate-50 focus:bg-white text-slate-800"
              />
              <p className="text-[11px] text-slate-400">
                Cole o endereço direto de uma imagem hospedada na web em PNG, SVG ou JPG.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsUrlModalOpen(false)}
                className="px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSalvarUrlLogo}
                className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-xs"
              >
                Aplicar Logo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: FICHA CADASTRAL COMPLETA DA EMPRESA */}
      {isFichaModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl p-6 space-y-6 my-auto">
            {/* Header Modal */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                {formData.logo_url ? (
                  <img
                    src={formData.logo_url}
                    alt={formData.nome_fantasia}
                    className="h-10 object-contain max-w-[120px]"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="p-2 bg-red-50 text-red-600 rounded-xl">
                    <Building2 className="w-6 h-6" />
                  </span>
                )}
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">
                    {formData.nome_fantasia || formData.razao_social}
                  </h3>
                  <p className="text-xs text-slate-500">Ficha Cadastral Oficial da Empresa</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleBaixarPdf}
                  disabled={gerandoPdf}
                  className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5 disabled:opacity-60"
                  title="Baixar Ficha Cadastral em PDF"
                >
                  {gerandoPdf ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-700" />
                  ) : (
                    <Download className="w-3.5 h-3.5 text-emerald-700" />
                  )}
                  <span>{gerandoPdf ? 'Gerando...' : 'Baixar PDF'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleImprimirFicha}
                  className="px-3 py-1.5 text-slate-700 bg-slate-100 hover:bg-slate-200 text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
                  title="Imprimir Ficha Cadastral"
                >
                  <Printer className="w-3.5 h-3.5 text-slate-600" />
                  <span>Imprimir</span>
                </button>

                <button
                  onClick={() => setIsFichaModalOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
                  title="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Ficha Resumo */}
            <div className="space-y-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Razão Social</span>
                    <span className="font-bold text-slate-900 text-sm">{formData.razao_social}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Nome Fantasia</span>
                    <span className="font-bold text-slate-900">{formData.nome_fantasia}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200/60">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">CNPJ</span>
                    <div className="flex items-center gap-1">
                      <span className="font-black text-slate-800">{formData.cnpj}</span>
                      <button
                        type="button"
                        onClick={() => handleCopiar(formData.cnpj, 'CNPJ')}
                        className="text-slate-400 hover:text-slate-700"
                        title="Copiar CNPJ"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Inscrição Estadual</span>
                    <span className="font-medium text-slate-800">{formData.inscricao_estadual || 'Isento / Não inf.'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase font-bold block">Registro ANTT</span>
                    <span className="font-bold text-red-600">{formData.rntrc_antt || 'Não inf.'}</span>
                  </div>
                </div>
              </div>

              {/* Contatos */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <h4 className="font-bold text-slate-700 uppercase tracking-wider text-[11px]">Canais de Contato</h4>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">Telefone</span>
                    <span className="font-semibold text-slate-800">{formData.telefone}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">WhatsApp</span>
                    <span className="font-semibold text-emerald-700">{formData.whatsapp}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">E-mail</span>
                    <span className="font-semibold text-slate-800 truncate block">{formData.email}</span>
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <h4 className="font-bold text-slate-700 uppercase tracking-wider text-[11px]">Endereço Completo</h4>
                <p className="font-medium text-slate-800">
                  {formData.logradouro}, nº {formData.numero} {formData.complemento ? `(${formData.complemento})` : ''} - {formData.bairro}
                  <br />
                  {formData.cidade} - {formData.uf} • CEP: {formData.cep}
                </p>
              </div>

              {/* Dados Bancários */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <h4 className="font-bold text-slate-700 uppercase tracking-wider text-[11px]">Dados para Faturamento & PIX</h4>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">Banco</span>
                    <span className="font-semibold text-slate-800">{formData.banco_padrao || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">Agência / Conta</span>
                    <span className="font-semibold text-slate-800">{formData.agencia} / {formData.conta_corrente}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">Chave PIX</span>
                    <div className="flex items-center gap-1">
                      <span className="font-black text-emerald-700 truncate">{formData.chave_pix || formData.cnpj}</span>
                      <button
                        type="button"
                        onClick={() => handleCopiar(formData.chave_pix || formData.cnpj, 'PIX')}
                        className="text-slate-400 hover:text-slate-700"
                        title="Copiar Chave PIX"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {copiadoTexto && (
                <div className="p-2 bg-emerald-100 text-emerald-800 font-bold text-center rounded-lg text-xs animate-fadeIn">
                  {copiadoTexto} copiado para a área de transferência!
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleBaixarPdf}
                  disabled={gerandoPdf}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5 shadow-xs disabled:opacity-60"
                >
                  {gerandoPdf ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  <span>{gerandoPdf ? 'Gerando Documento PDF...' : 'Baixar PDF'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleImprimirFicha}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
                >
                  <Printer className="w-3.5 h-3.5 text-slate-600" />
                  <span>Imprimir Ficha</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setIsFichaModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CONFIRMAR EXCLUSÃO DE EMPRESA */}
      {isExcluirModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6 space-y-4 animate-scaleUp">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-rose-100 text-rose-600 rounded-2xl shrink-0">
                <Trash2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Excluir Unidade / Empresa</h3>
                <p className="text-xs text-slate-500">Confirmação de exclusão permanente</p>
              </div>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-700 space-y-1.5">
              <p>
                Tem certeza que deseja excluir os dados da empresa <strong>{formData.nome_fantasia || formData.razao_social}</strong>?
              </p>
              <p className="text-slate-500 text-[11px]">
                CNPJ: <span className="font-semibold text-slate-700">{formData.cnpj || 'Não informado'}</span>
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                disabled={isExcluindo}
                onClick={() => setIsExcluirModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isExcluindo}
                onClick={handleConfirmarExcluirEmpresa}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-xs transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {isExcluindo ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Excluindo Empresa...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Sim, Excluir Empresa</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CONFIRMAR REMOÇÃO DA LOGO */}
      {isRemoverLogoModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-rose-100 text-rose-600 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Remover Logo</h3>
                <p className="text-xs text-slate-500">Retirar a imagem cadastrada desta unidade</p>
              </div>
            </div>

            <p className="text-xs text-slate-600">
              Deseja realmente retirar a logomarca da empresa <strong>{formData.nome_fantasia}</strong>?
            </p>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsRemoverLogoModalOpen(false)}
                className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmarRemoverLogo}
                className="px-3 py-1.5 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-xs transition"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
